import Bugsnag from '@bugsnag/js'
import { createInstance, type Module } from 'i18next'
import Backend from 'i18next-http-backend'
import ICU from 'i18next-icu'
import { initReactI18next } from 'react-i18next'

import { assetUrl } from '@cash-web/shared-util-url'

/**
 * Setup ICU plugin to work correctly with our i18n configuration.
 * Don't let the ICU plugin override i18next's key lookup logic,
 * because we include context in our keys.
 * https://github.com/i18next/i18next-icu/blob/master/src/index.js#L76-L80
 * https://github.com/i18next/i18next/blob/master/src/Translator.js#L469-L498
 */
delete ICU.prototype.addLookupKeys

const i18n = createInstance()

/**
 * Must stay in sync with `LEGAL_SEPARATOR` in `tools/i18n/post-processors/legal-suffix.js`,
 * which is the producer of this marker. `legal-suffix.spec.ts` pins the two together — the pin
 * lives on the tools side because a reference from here to that CJS build script would add an
 * Nx dependency edge dragging the whole tools project into the i18n extraction scope of every
 * app that depends on this lib.
 */
export const LEGAL_SEPARATOR = '|░legal░|'

/**
 * i18next-parser suffixes each plural category's catalog key with `_` (`key_one`, `key_other`), and
 * Atlas translates those keys, so lookups must use the same separator. Pinned by
 * `tools/i18n/i18next-parser.config.base.spec.ts`.
 */
export const PLURAL_SEPARATOR = '_'

const warnLegalKeyCollision = (baseKey: string) => {
  if (process.env['NODE_ENV'] === 'production') return

  // eslint-disable-next-line no-console
  console.warn(
    `i18n: locale bundle carries both "${baseKey}" and "${baseKey}${LEGAL_SEPARATOR}". ` +
      'Using the legal-marked value; the plain key is stale and should disappear once Atlas catches up.'
  )
}

/**
 * Keys for strings marked `legal: true` carry a trailing `|░legal░|` marker so the
 * translation pipeline can route them to a legal translation service. i18next builds
 * its lookup key from the source string plus context only, so the marker has to be
 * stripped before a bundle reaches the resource store — otherwise every legal string
 * misses and falls back to English.
 *
 * Both forms of a key can briefly co-exist in a translated bundle: `legal-suffix.js` dedupes
 * base keys in en-US only, so a freshly-marked key can land while Atlas still serves the plain
 * one. The marked key is the one the pipeline actively maintains, so it wins regardless of
 * which form the bundle happens to list first.
 */
export const stripLegalKeyMarkers = (bundle: Record<string, unknown>): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(bundle)) {
    const isMarked = key.endsWith(LEGAL_SEPARATOR)
    const baseKey = isMarked ? key.slice(0, -LEGAL_SEPARATOR.length) : key

    if (baseKey in normalized) {
      warnLegalKeyCollision(baseKey)
      if (!isMarked) continue
    }

    normalized[baseKey] = value
  }

  return normalized
}

type BackendOptions = typeof Backend.prototype.options

/**
 * Exported so the parse hook — the line that actually applies the legal-marker strip to a
 * loaded bundle — is reachable from a test without standing up an HTTP backend.
 */
export const createBackendConfig = (application: string, icu: boolean): BackendOptions => {
  // For supported apps, load translations from S3/CDN with dynamic locale.
  // Return a path with placeholders; the backend interpolates it with lng and ns at load time.
  const lngPlaceholder = icu ? '{lng}' : '{{lng}}'
  const nsPlaceholder = icu ? '{ns}' : '{{ns}}'

  return {
    loadPath: () =>
      assetUrl({
        pathPrefix: `apps/i18n-assets/assets/locales/${lngPlaceholder}`,
        filename: `${nsPlaceholder}.json`,
        namespace: application,
      }),
    parse: (data: string) => stripLegalKeyMarkers(JSON.parse(data)),
  }
}

interface i18nOptions {
  locale?: string
  icu?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plugins?: any[]
  backend?: BackendOptions
}

const detectResolvedLocale = (defaultLocale: string): string => {
  let locale = defaultLocale
  if (typeof window !== 'undefined') {
    if (window?.locale) {
      locale = window?.locale
    }
    // set lang attribute on <html> element
    document.documentElement.lang = locale
  }

  return locale
}

const createLanguageDetector = (defaultLocale: string) =>
  ({
    type: 'languageDetector',
    init: () => undefined,
    cacheUserLanguage: () => undefined,
    detect: () => detectResolvedLocale(defaultLocale),
  }) as Module

export const i18next = (applicationName: string, options: i18nOptions = {}) => {
  const { locale: localeOption, icu = true, plugins } = options
  const locale = localeOption || 'en-US'

  // If already initialized, return the existing instance
  // prevents multiple calls to i18next when developing locally
  if (i18n.isInitialized) {
    return Promise.resolve(i18n)
  }

  let i18nInstance = i18n.use(createLanguageDetector(locale)).use(initReactI18next)

  const thirdPartyModules = plugins || []

  if (icu) {
    thirdPartyModules.push(ICU)
  }

  thirdPartyModules?.forEach(plugin => {
    i18nInstance = i18nInstance.use(plugin)
  })

  // Alias application names if needed for S3 bucket references
  // e.g. account app is named 'account-shell' within cash-web, but the S3 bucket is named 'account', so we need to alias it.
  const appNameToS3Bucket = { 'account-shell': 'account', 'standalone-pages': 'standalone' } as {
    [key: string]: string
  }

  const application = appNameToS3Bucket[applicationName] || applicationName

  // supported namespaces are apps that load translations from S3/CDN for multiple locales.
  // unsupported apps will use fallback behavior (translation key as value).
  const supportedNamespaces = [
    'account-shell',
    'cashtag-p2p',
    'checkout-v2',
    'complaints',
    'lightning',
    'login',
    'marketing-apps',
    'neighborhood-profiles',
    'neighborhood-profiles-storefront-login',
    'pools',
    'staff',
    'standalone-pages',
    'support',
  ]

  let backendConfig: BackendOptions | undefined
  // Only use HTTP backend on the client side to avoid SSR hangs/timeouts
  // During SSR, i18next will use fallback behavior (translation key as value)
  const isClientSide = typeof window !== 'undefined'
  if (supportedNamespaces.includes(applicationName) && isClientSide) {
    // The Backend plugin is used to load translation files for supported apps.
    i18nInstance = i18nInstance.use(Backend)

    backendConfig = createBackendConfig(application, icu)
  }

  return i18nInstance.init(
    {
      load: 'currentOnly',
      fallbackLng: 'en-US',
      debug: process.env['DEBUG_I18N'] === 'true',
      defaultNS: applicationName,
      ns: [applicationName],
      keySeparator: false,
      nsSeparator: false,
      contextSeparator: '|░context░|',
      pluralSeparator: PLURAL_SEPARATOR,
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
        prefix: icu ? '{' : '{{',
        suffix: icu ? '}' : '}}',
      },
      ...(backendConfig ? { backend: backendConfig } : {}),
      missingKeyHandler: (lngs, ns, key) => {
        const message = `Missing translation in lngs: ${lngs.join(', ')}, namespace: ${ns}, key: ${key}`
        // eslint-disable-next-line no-console
        if (typeof window === 'undefined') console.error(message)

        Bugsnag.notify(new Error(`${applicationName}: ${message}`))
      },
    },
    error => {
      if (error) {
        const message = `i18next failed to initialize: ${error}`

        // eslint-disable-next-line no-console
        console.error(message)
        Bugsnag.notify(new Error(message))
      }
    }
  )
}
